"use client";

import { useMediaQuery } from "modules/common/hooks/useMediaQuery";
import { useMemo } from "react";

/**
 * Responsive settings for charts based on screen size
 */
interface ChartSettings {
  height: number;
  marginTop: number;
  marginRight: number;
  marginBottom: number;
  marginLeft: number;
  tickCount: number;
  pointSize: number;
  fontSize: string;
}

/**
 * Hook that returns optimized chart settings based on the current screen size
 */
export function useResponsiveChartSettings(): ChartSettings {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const isTablet = useMediaQuery("(max-width: 1024px)");

  return useMemo(() => {
    if (isMobile) {
      return {
        height: 200,
        marginTop: 12,
        marginRight: 8,
        marginBottom: 22,
        marginLeft: 32,
        tickCount: 3,
        pointSize: 5,
        fontSize: "text-xs",
      };
    } else if (isTablet) {
      return {
        height: 240,
        marginTop: 12,
        marginRight: 12,
        marginBottom: 26,
        marginLeft: 40,
        tickCount: 4,
        pointSize: 6,
        fontSize: "text-sm",
      };
    } else {
      return {
        height: 280,
        marginTop: 16,
        marginRight: 16,
        marginBottom: 32,
        marginLeft: 50,
        tickCount: 5,
        pointSize: 6,
        fontSize: "text-sm",
      };
    }
  }, [isMobile, isTablet]);
}

/**
 * Hook for determining how many X-axis points to show based on screen size and data length
 */
export function useVisibleAxisPoints(
  dataLength: number
): (i: number) => boolean {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const isTablet = useMediaQuery("(max-width: 1024px)");

  return useMemo(() => {
    return (index: number) => {
      if (dataLength === 0) return false;

      const first = 0;
      const last = dataLength - 1;

      // Always show first and last points
      if (index === first || index === last) return true;

      // Determine max labels based on screen size
      let maxLabels = 7; // Desktop default
      if (isMobile) {
        maxLabels = 3; // Total: first, middle, last
      } else if (isTablet) {
        maxLabels = 5; // Total: first, 3 intermediate, last
      }

      // Calculate increment to ensure we don't exceed maxLabels
      // We already show first and last, so we need maxLabels - 2 intermediate points
      const increment = Math.max(1, Math.ceil(dataLength / maxLabels));

      // Show points at regular intervals
      return index % increment === 0;
    };
  }, [dataLength, isMobile, isTablet]);
}
